import { Injectable } from '@angular/core';
import { IIdentifiable } from 'app/shared/interfaces/identifiable.interface';

@Injectable({
    providedIn: 'root',
})
export class EnumService {
    /**
     * True if the given value is a value of the specified Enum; otherwise, false.
     * @param enumObj
     * @param value
     */
    public isValueOfEnum<T>(enumObj: T, value: any): value is T[keyof T] {
        return Object.values(enumObj).includes(value);
    }

    /**
     * Get the enum key string by the specified value.
     * @param enumObj
     * @param value
     */
    public getEnumKeyByValue<T extends { [index: string]: string | number }>(enumObj: T,
                                                                             value: string | number,
    ): keyof T | null {
        // Check if the value is actually a key in the enum
        if (typeof value === 'string' && value in enumObj) {
            return value as keyof T;
        }

        // Otherwise, try to find the key by the value
        for (const [key, val] of Object.entries(enumObj)) {
            if (val === value) {
                return key as keyof T;
            }
        }

        return null;
    }

    /**
     * Get enum value by the specified key.
     *
     * @param enumType
     * @param key
     */
    public getEnumByKey<T extends object>(enumType: T, key: string | number): T[keyof T] | undefined {
        if (key in enumType) {
            return enumType[key as keyof T];
        }

        return undefined;
    }

    public getEnumByValue<T>(enumType: T, value: string | number): T[keyof T] | undefined {
        return this.safeCastToEnum(enumType, value);
    }

    public safeCastToEnum<T>(enumType: T, value: string | number): T[keyof T] | undefined {
        return Object.values(enumType).includes(value) ? (value as T[keyof T]) : undefined;
    }

    public getEnumDefinitionFromKey<T extends Record<string, string | number>>(enumType: T, value: string | number): string {
        const enumKey = this.getEnumKeyByValue(enumType, value);
        if (!enumKey) {
            return value?.toString();
        }

        const enumKeyString = String(enumKey);
        const definition = enumKeyString
            .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase/PascalCase
            .replace(/[_-]/g, ' ')               // snake_case / kebab-case
            .trim();
        return this.toTitleCase(definition);
    }

    /**
     * Transform a numeric enum into an array of identifiable objects.
     *
     * This function filters, sorts, and maps the values of a numeric enum to a more usable format,
     * returning an array of objects where each object contains the enum value as `id` and the corresponding
     * enum key as `name`. The function is designed to handle only numeric enums, filtering out reverse
     * mappings and skipping specified enum values.
     *
     * @template T - A numeric type representing the enum values.
     *
     * @param enumType - The enum to be transformed, provided as an object where the keys are the enum names and values are numeric.
     * @param enumSort - An optional array that specifies the desired order of the enum values. If provided, the resulting array will be sorted according to this order.
     * @param skipValues - An optional array of enum values to be excluded from the result. Any values in this array will not appear in the returned list.
     *
     * @returns An array of objects where each object has an `id` property corresponding to the enum value and a `name` property corresponding to the enum key.
     *
     * @example
     * enum OrderCategory {
     *   UNSPECIFIED = 0,
     *   ELECTRONICS = 1,
     *   BOOKS = 2,
     *   FASHION = 3,
     * }
     *
     * const categoriesSort = [OrderCategory.FASHION, OrderCategory.BOOKS, OrderCategory.ELECTRONICS];
     * const skipValues = [OrderCategory.UNSPECIFIED];
     *
     * const result = transformEnumToIdentifiable(OrderCategory, categoriesSort, skipValues);
     * console.log(result);
     * // Output:
     * // [
     * //   { id: 3, name: 'FASHION' },
     * //   { id: 2, name: 'BOOKS' },
     * //   { id: 1, name: 'ELECTRONICS' }
     * // ]
     */
    public transformEnumToIdentifiables<T extends string | number>(
        enumType: { [key: string]: T },
        enumSort: T[] = [],
        skipValues: T[] = [],
    ): IIdentifiable[] {
        const filteredTypes: T[] =
            Object.keys(enumType)
                  .filter(key => isNaN(Number(key))) // Filter out reverse mapping for numeric enums.
                  .filter(key => !skipValues.includes(enumType[key as keyof typeof enumType]))
                  .map(key => enumType[key as keyof typeof enumType]);

        const sortedTypes = filteredTypes || [];
        sortedTypes.sort((a: T, b: T) => {
            return enumSort.indexOf(a) - enumSort.indexOf(b);
        });

        return sortedTypes.map(item => ({
            id: item,
            name: Object.keys(enumType).find(key => enumType[key as keyof typeof enumType] === item) || '',
        }));
    }

    /**
     * Transforms an array of enum values into an object where each key is the name of the enum member in uppercase,
     * and the value is the corresponding enum value.
     *
     * @template T - The enum type.
     * @param {T} enumType - The enum type to transform.
     * @param {T[keyof T][]} enumArray - An array of enum values to be transformed.
     * @returns {{ [key: string]: number }} - An object where the keys are the uppercase names of the enum members,
     * and the values are the corresponding enum values.
     *
     * @example
     * enum Category {
     *   Order = 1,
     *   Complete = 2,
     *   Cancelled = 3
     * }
     *
     * const categoryArray: Category[] = [Category.Order, Category.Complete];
     * const result = transformEnumArrayToObject(Category, categoryArray);
     * // result: { ORDER: 1, COMPLETE: 2 }
     *
     * @example
     * enum Status {
     *   Pending = 10,
     *   Approved = 20,
     *   Rejected = 30
     * }
     *
     * const statusArray: Status[] = [Status.Pending, Status.Approved];
     * const result = transformEnumArrayToObject(Status, statusArray);
     * // result: { PENDING: 10, APPROVED: 20 }
     */
    public transformEnumArrayToObject<T>(enumType: T, enumArray: T[keyof T][]): { [key: string]: number } {
        return enumArray.reduce((acc, enumValue) => {
            // Get the enum key (name) corresponding to the enum value
            const enumKey = Object.keys(enumType).find(key => enumType[key as keyof T] === enumValue);

            if (enumKey) {
                // Add the uppercase key to the accumulator object with its corresponding value
                acc[enumKey.toUpperCase()] = enumValue as unknown as number;
            }

            return acc;
        }, {} as { [key: string]: number });
    }

    private toTitleCase(input: string): string {
        return input
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
